import React, {useEffect, useState} from 'react';

import isArchived from 'archive/isArchived';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {GRID_SCHEMA_CLASS_TO_TYPE_TEXT, GridSchemaClass} from 'constants/SampleConstants';
import FieldTable, {ClientSchemaField} from 'registry/field/FieldTable';
import {CONFIRM_DELETE_ALL} from 'registry/field/FieldTableConstants';
import StorableFieldTableRow from 'registry/field/StorableFieldTableRow';
import * as RegistryUtil from 'registry/RegistryUtil';
import ContainerSchemaSelector from 'samples/ContainerSchemaSelector';
import {CreateGridSchemaParams, UpdateGridSchemaParams} from 'samples/SampleSchemaActions';
import {
  ContainerSchema,
  GridSchema,
  PLATE_SCHEMA_TYPE_TO_TEXT,
} from 'samples/storables/StorableSchemaConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import SchemaWarehouseNameField from 'schemas/SchemaWarehouseNameField';
import {
  StorableSchemaSettingProps,
  StorableSchemaSettingsQueryingProps as QueryingProps,
  useQueryingStorableSchemaSettings,
} from 'settings/samples/StorableSchemaSettingsConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {TextFieldType} from 'ui-library/inputs/EditableTextField';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import SettingsField, {SettingsLabel} from 'ui-library/SettingsField';
import * as modals from 'util/Modals';
import {ElementMouseEvent, MakeFieldsOptional} from 'util/Types';

import getPresetPlateSizes from './getPresetPlateSizes';

export interface GridSchemaSettingsProps extends StorableSchemaSettingProps {
  containerSchemas: Array<ContainerSchema>;
  gridSchema?: GridSchema | null;
  gridSchemaClass: GridSchemaClass;
  onCreate: (gridSchema: CreateGridSchemaParams) => void;
  onUpdate: (gridSchema: UpdateGridSchemaParams) => void;
}

type GridSchemaSettingsState = MakeFieldsOptional<
  Omit<GridSchema, 'allFields'>,
  'id' | 'registryId' | 'iconId' | 'fields'
> & {
  allFields: Array<SchemaField | ClientSchemaField>;
};

function GridSchemaSettings(props: GridSchemaSettingsProps & QueryingProps): JSX.Element {
  const [gridSchema, setGridSchema] = useState<GridSchemaSettingsState>(
    props.gridSchema ?? {
      name: '',
      prefix: '',
      sqlIdentifier: '',
      height: (props.gridSchemaClass as string) === (ModelTypeConstants.PLATE_SCHEMA as string) ? 8 : 10,
      width: (props.gridSchemaClass as string) === (ModelTypeConstants.PLATE_SCHEMA as string) ? 12 : 10,
      containerSchemaId: null,
      allFields: [],
    }
  );

  const isWritable = (): boolean => props.isWritable && !isArchived({...gridSchema, id: gridSchema.id ?? ''});

  useEffect(() => {
    if (props.gridSchema) {
      setGridSchema(props.gridSchema);
    }
  }, [props.gridSchema]);

  const handleChangeSchema = (update: Partial<GridSchemaSettingsState>): void => {
    const updatedGridSchema = {...gridSchema, ...update};
    setGridSchema(updatedGridSchema);
    if (!props.isBeingCreated) {
      handleSubmit(null, updatedGridSchema);
    }
  };

  const handleSubmit = (e: ElementMouseEvent | null, gridSchemaToSubmit?: GridSchemaSettingsState): void => {
    gridSchemaToSubmit = gridSchemaToSubmit ?? gridSchema;
    const fields = RegistryUtil.sanitizeFields(gridSchemaToSubmit.allFields);
    const parsedGridSchema = {...gridSchemaToSubmit, allFields: fields, fields};
    if (props.isBeingCreated) {
      props.onCreate(parsedGridSchema);
      return;
    }
    props.onUpdate(parsedGridSchema);
  };

  const handleChangePrefix = (prefix: string): void => {
    handleChangeSchema({prefix});
  };

  const handleChangeName = (name: string): void => {
    handleChangeSchema({name});
  };

  const handleChangeSqlIdentifier = (sqlIdentifier: string): void => {
    handleChangeSchema({sqlIdentifier});
  };

  const handleChangeHeight = (height: number): void => {
    handleChangeSchema({height});
  };

  const handleChangeWidth = (width: number): void => {
    handleChangeSchema({width});
  };

  const handleChangeType = (plateSchemaType: string): void => {
    handleChangeSchema({plateSchemaType});
  };

  const handleChangeContainerSchema = (selectedContainerSchema: ContainerSchema): void => {
    handleChangeSchema({
      containerSchemaId: selectedContainerSchema ? selectedContainerSchema.id : null,
    });
  };

  const handleChangeFields = (allFields: Array<ClientSchemaField>): void => {
    handleChangeSchema({allFields});
  };

  const confirmDeleteFields = (onSubmit: (param?: string) => void): void => {
    modals
      .confirm(
        'Confirm Delete',
        `Are you sure you want to delete this plate field?
         All values corresponding to this plate field will be deleted. This action cannot be undone.`,
        'danger'
      )
      .done(() => {
        onSubmit(CONFIRM_DELETE_ALL);
      });
  };

  const getSizeName = (height: number, width: number): string => `${height * width} (${height} x ${width})`;

  const plateSchemaTypeOptions = Object.keys(PLATE_SCHEMA_TYPE_TO_TEXT).map((type) => ({
    name: PLATE_SCHEMA_TYPE_TO_TEXT[type],
    handleOnClick: () => {
      handleChangeType(type);
    },
  }));
  const plateSchemaSizeOptions = getPresetPlateSizes().map(([height, width]: Array<number>) => ({
    name: getSizeName(height, width),
    handleOnClick: () => {
      handleChangeSchema({height, width});
    },
  }));

  return (
    <div className="u-flex-grow">
      <div className="settingsPageContainer-section">
        <Row>
          <Col sm={4}>
            <SettingsField
              isBeingCreated={props.isBeingCreated}
              isRequired={true}
              isWritable={isWritable()}
              label="Prefix"
              name="prefix"
              onChange={handleChangePrefix}
              value={gridSchema.prefix}
            />
          </Col>
          <Col sm={props.gridSchema ? 4 : 8}>
            <SettingsField
              isBeingCreated={props.isBeingCreated}
              isRequired={true}
              isWritable={isWritable()}
              label="Name"
              name="name"
              onChange={handleChangeName}
              value={gridSchema.name}
            />
          </Col>
          {props.gridSchema && (
            <Col sm={4}>
              <SchemaWarehouseNameField
                onChange={handleChangeSqlIdentifier}
                isWritable={isWritable() && !props.isBeingCreated}
                sqlIdentifier={gridSchema.sqlIdentifier}
              />
            </Col>
          )}
        </Row>
      </div>
      <div className="settingsPageContainer-section">
        <Row>
          {(props.gridSchemaClass as string) === (ModelTypeConstants.PLATE_SCHEMA as string)
            ? [
                <Col sm={4} key="size">
                  <div>
                    <SettingsLabel label="Size" name="size" isRequired={true} />
                    <SelectDropdown
                      dataTestAttrs={{component: 'PlateSchemaSizeSelector', element: 'input'}}
                      disabled={!isWritable()}
                      displayName={getSizeName(gridSchema.height, gridSchema.width)}
                      menu={
                        <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL}>
                          {plateSchemaSizeOptions.map((size) => (
                            <Option key={size.name} onSelect={size.handleOnClick} value={size}>
                              {size.name}
                            </Option>
                          ))}
                        </Menu>
                      }
                    />
                  </div>
                </Col>,
                <Col sm={4} key="type">
                  <SettingsLabel label="Type" name="type" isRequired={true} />
                  <SelectDropdown
                    dataTestAttrs={{component: 'PlateSchemaTypeSelector', element: 'input'}}
                    disabled={!isWritable()}
                    displayName={
                      PLATE_SCHEMA_TYPE_TO_TEXT[gridSchema.plateSchemaType ?? ''] || 'Select type...'
                    }
                    menu={
                      <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL}>
                        {plateSchemaTypeOptions.map((type) => (
                          <Option key={type.name} onSelect={type.handleOnClick} value={type}>
                            {type.name}
                          </Option>
                        ))}
                      </Menu>
                    }
                  />
                </Col>,
              ]
            : [
                <Col sm={4} key="height">
                  <div className="u-lr-split">
                    <div className="u-flex-grow">
                      <SettingsField
                        isBeingCreated={props.isBeingCreated}
                        isRequired={true}
                        isWritable={isWritable()}
                        label="Height"
                        name="height"
                        type={TextFieldType.NUMBER}
                        onChange={handleChangeHeight}
                        value={gridSchema.height}
                      />
                    </div>
                  </div>
                </Col>,
                <Col sm={4} key="width">
                  <div className="u-lr-split">
                    <div className="u-flex-grow u-tiny-spacing-left">
                      <SettingsField
                        isBeingCreated={props.isBeingCreated}
                        isRequired={true}
                        isWritable={isWritable()}
                        label="Width"
                        name="width"
                        type={TextFieldType.NUMBER}
                        onChange={handleChangeWidth}
                        value={gridSchema.width}
                      />
                    </div>
                  </div>
                </Col>,
              ]}
          <Col sm={4} dataTestAttrs={{component: 'ContainerSchemaSelector', element: 'root'}}>
            <SettingsLabel label="Container Schema" name="containerSchema" />
            <ContainerSchemaSelector
              isDisabled={!isWritable()}
              onChange={handleChangeContainerSchema}
              registryId={props.registryId}
              selectedContainerSchemaId={gridSchema.containerSchemaId}
            />
          </Col>
        </Row>
      </div>
      <div className="settingsPageContainer-section">
        <Row>
          <Col sm={12}>
            <FieldTable // TODO (BNCH-53233): Render FieldTableLoading while fetching data
              schemaId={gridSchema.id ?? null}
              schema={props.gridSchema}
              isBeingCreated={props.isBeingCreated}
              shouldShowIsIntegration={false}
              shouldShowIsRequired={true}
              shouldShowIsParentLink={false}
              shouldShowTooltipText={true}
              isDisabled={!isWritable()}
              fields={gridSchema.allFields}
              itemClass={StorableFieldTableRow}
              onFieldsChange={handleChangeFields}
              registryId={props.registryId}
              ownerId={props.registry.ownerJson.id}
              ownerUrlId={props.ownerUrlId}
              savingState={props.savingState}
              schemaType={GRID_SCHEMA_CLASS_TO_TYPE_TEXT[props.gridSchemaClass]}
              handleDeleteConfirmation={confirmDeleteFields}
              storableSchemas={props.storableSchemas}
            />
          </Col>
        </Row>
      </div>
      {props.isBeingCreated && (
        <Button
          buttonStyle={ButtonStyle.PRIMARY}
          disabled={!isWritable()}
          onClick={handleSubmit}
          size={ButtonSize.MEDIUM}
          text="Create"
        />
      )}
    </div>
  );
}

GridSchemaSettings.defaultProps = {
  containerSchemas: [],
};

export default function QueryingGridSchemaSettings(props: GridSchemaSettingsProps): JSX.Element | null {
  return useQueryingStorableSchemaSettings(props, (queryingProps) => (
    <GridSchemaSettings {...props} {...queryingProps} />
  ));
}
